import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { formatTimeBasedOnTimezone } from '../../core/utils/date-time.util';
import { SharedModule } from '../../shared/shared.module';
import { MentorPanelService } from '../../core/services/mentor-panel/mentor-panel.service';
import { MentorBookingItem, MentorSessionRequest, SessionStatus } from '../../core/models/mentor-panel/mentor-panel-api-models';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { StatusBadgeComponent } from '../../shared/status-badge/status-badge.component';

@Component({
    selector: 'app-session-requests',
    standalone: true,
    imports: [CommonModule, SharedModule, StatusBadgeComponent],
    templateUrl: './session-requests.component.html',
    styleUrl: './session-requests.component.scss'
})
export class SessionRequestsComponent implements OnInit {
    private mentorPanelService = inject(MentorPanelService);
    private dialog = inject(MatDialog);
    requests: MentorSessionRequest[] = [];
    isLoading: boolean = true;
    readonly SessionStatus = SessionStatus;

    searchQuery: string = '';
    activeFilter: string = 'All';

    constructor() { }

    get filteredRequests(): MentorSessionRequest[] {
        if (!this.searchQuery) {
            return this.requests;
        }
        const query = this.searchQuery.toLowerCase().trim();
        return this.requests.filter(request =>
            (request.studentName || '').toLowerCase().includes(query) ||
            (request.topic || '').toLowerCase().includes(query)
        );
    }

    ngOnInit(): void {
        this.fetchRequests();
    }

    private fetchRequests(): void {
        this.mentorPanelService.getMentorBookings().subscribe({
            next: (response) => {
                if (response.Success) {
                    this.requests = response.Data.bookings
                        .map(b => this.mapToRequest(b));
                }
                this.isLoading = false;
            },
            error: (err) => {
                console.error('Error fetching requests', err);
                this.isLoading = false;
            }
        });
    }

    private mapToRequest(item: MentorBookingItem): MentorSessionRequest {
        return {
            id: item.booking_id,
            studentName: item.student_name,
            initials: this.getInitials(item.student_name),
            date: item.appointment_date,
            time: this.formatTimeTo12h(item.start_time),
            duration: `${item.duration_minutes} min`,
            topic: item.topics,
            status: item.status,
            category: item.is_free ? 'FREE' : 'PAID',
            avatarColorClass: this.getRandomAvatarColor(item.student_name)
        };
    }

    private getInitials(name: string): string {
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    }

    private getRandomAvatarColor(name: string): string {
        const colors = ['blue-avatar', 'purple-avatar', 'orange-avatar', 'green-avatar'];
        const index = name.length % colors.length;
        return colors[index];
    }

    private formatTimeTo12h(isoString: string): string {
        return formatTimeBasedOnTimezone(isoString);
    }

    acceptRequest(request: MentorSessionRequest) {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                title: 'Accept Request',
                message: `Are you sure you want to accept the session request from ${request.studentName}?`,
                confirmText: 'Accept',
                cancelText: 'Cancel',
                type: 'submit'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.mentorPanelService.updateBookingStatus(request.id, { action: true }).subscribe({
                    next: (response) => {
                        if (response.Success) {
                            this.fetchRequests();
                        }
                    }
                });
            }
        });
    }

    rejectRequest(request: MentorSessionRequest) {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                title: 'Reject Request',
                message: `Are you sure you want to reject the session request from ${request.studentName}?`,
                confirmText: 'Reject',
                cancelText: 'Cancel',
                type: 'quit'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.mentorPanelService.updateBookingStatus(request.id, { action: false }).subscribe({
                    next: (response) => {
                        if (response.Success) {
                            this.fetchRequests();
                        }
                    }
                });
            }
        });
    }



    setFilter(filter: string) {
        this.activeFilter = filter;
    }
}
